import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, Button, Form } from 'react-bootstrap';
import { resetSystemTheme } from '../utils/ecommerceHelper';

const Layout = ({ children, user, onLogout, onChangeCompany }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fechaHora, setFechaHora] = useState(new Date());
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const isHome = location.pathname === '/home';

  // El reloj ahora vive acá, así se ve en TODAS las pantallas
  useEffect(() => {
    const timer = setInterval(() => setFechaHora(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Garantizar que en el sistema ERP SISGECOM se preserven siempre los colores nativos
  useEffect(() => {
    resetSystemTheme();
  }, [location.pathname, user?.empresa_activa]);

  // Seguridad para que no explote si user es null
  const nombreUsuario = user?.nombre || "Usuario";
  const rolUsuario = user?.rol || "Admin";
  const empresas = user?.empresas || [];
  const empresaActiva = user?.empresa_activa;

  // Modal para seleccionar empresa al ingresar al sistema
  const [showCompanyModal, setShowCompanyModal] = useState(() => {
    try {
      const alreadyChosen = sessionStorage.getItem('companySelectedInSession');
      return !alreadyChosen && (empresas.length > 1);
    } catch {
      return false;
    }
  });

  const [modalCompanyId, setModalCompanyId] = useState(() => {
    return String(empresaActiva?.c_nuges || empresaActiva?.id || (empresas.find(e => String(e.id) === '5' || String(e.c_nuges) === '5')?.id || (empresas[0]?.id || '')));
  });

  useEffect(() => {
    if (empresaActiva) {
      setModalCompanyId(String(empresaActiva.c_nuges || empresaActiva.id));
    }
  }, [empresaActiva]);

  useEffect(() => {
    try {
      const alreadyChosen = sessionStorage.getItem('companySelectedInSession');
      if (!alreadyChosen && empresas.length > 1) {
        setShowCompanyModal(true);
      }
    } catch {}
  }, [empresas.length]);

  const handleConfirmCompanyModal = () => {
    if (modalCompanyId) {
      onChangeCompany(modalCompanyId);
    }
    try {
      sessionStorage.setItem('companySelectedInSession', 'true');
    } catch {}
    setShowCompanyModal(false);
  };

  const handleCompanyChange = (e) => {
    const val = e.target.value;
    onChangeCompany(val);
    navigate('/home');
    window.location.reload();
  };

  return (
    <div className="bg-light min-vh-100 d-flex flex-column">
      {/* NAVBAR FIJA */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark shadow">
        <div className="container-fluid px-4">
          <span 
            className="navbar-brand fw-bold text-primary" 
            style={{cursor: 'pointer'}} 
            onClick={() => navigate('/home')}
          >
            SISGECOM <small className="text-white fw-light">v1.0</small>
          </span>

          {!isHome && (
            <button 
              className="btn btn-outline-primary btn-sm ms-3 d-flex align-items-center"
              onClick={() => navigate('/home')}
            >
              <i className="bi bi-arrow-left-circle me-1"></i> Volver a Inicio
            </button>
          )}

          {/* Selector o Display de Empresa */}
          {empresas.length > 1 ? (
            <div className="ms-4 d-flex align-items-center">
              <i className="bi bi-building text-white-50 me-2"></i>
              <select 
                className="form-select form-select-sm bg-dark text-white border-secondary"
                value={String(empresaActiva?.c_nuges ?? empresaActiva?.id ?? '')}
                onChange={handleCompanyChange}
                style={{minWidth: '200px'}}
              >
                {empresas.map((emp) => {
                  const compVal = String(emp.c_nuges ?? emp.id);
                  return (
                    <option key={compVal} value={compVal}>
                      {emp.nombre}
                    </option>
                  );
                })}
              </select>
            </div>
          ) : empresas.length === 1 ? (
            <div className="ms-4 d-flex align-items-center">
              <span className="badge bg-secondary d-flex align-items-center py-2 px-3">
                <i className="bi bi-building me-2"></i>
                {empresaActiva?.nombre || empresas[0].nombre}
              </span>
            </div>
          ) : null}

          
          <div className="d-flex align-items-center ms-auto">
            {/* Reloj Digital */}
            <span className="text-info me-4 d-none d-lg-inline fw-mono">
              <i className="bi bi-clock me-2"></i>
              {fechaHora.toLocaleDateString()} - {fechaHora.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
            </span>

            <span className="text-white me-3 d-none d-md-inline">
              Bienvenido, <strong>{nombreUsuario}</strong> <small className="text-white-50">({rolUsuario})</small>
            </span>
            
            <button className="btn btn-outline-danger btn-sm" onClick={() => setShowLogoutModal(true)}>
              Salir
            </button>
          </div>
        </div>
      </nav>

      {/* CONTENIDO DINÁMICO (Home, AddressBook, etc.) */}
      <main className="flex-grow-1">
        {children}
      </main>

      {/* FOOTER FIJO */}
      <footer className="py-3 bg-white border-top mt-auto">
        <div className="container-fluid px-4 text-center">
          <small className="text-muted">SISGECOM - Sistema de Gestión Comercial</small>
        </div>
      </footer>

      {/* MODAL CONFIRMACIÓN DE SALIDA */}
      {showLogoutModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050 }}>
          <div className="modal-dialog modal-sm modal-dialog-centered text-center">
            <div className="modal-content border-0 shadow-lg p-3">
              <div className="modal-body">
                <i className="bi bi-box-arrow-right text-danger display-4 mb-3 d-block"></i>
                <p className="fs-5 mb-0">¿Cerrar sesión?</p>
              </div>
              <div className="modal-footer justify-content-center border-0 gap-2">
                <button className="btn btn-secondary px-3" onClick={() => setShowLogoutModal(false)}>Cancelar</button>
                <button className="btn btn-danger px-3" onClick={() => { onLogout(); navigate('/login'); }}>Sí, salir</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SELECCIÓN DE EMPRESA AL INGRESAR */}
      <Modal 
        show={showCompanyModal} 
        backdrop="static" 
        keyboard={false} 
        centered
      >
        <Modal.Header className="bg-primary text-white py-3">
          <Modal.Title className="fs-5 d-flex align-items-center">
            <i className="bi bi-building me-2 fs-4"></i>
            Seleccionar Empresa de Trabajo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="text-muted mb-3">
            Bienvenido, <strong>{nombreUsuario}</strong>. Seleccione la empresa en la que desea operar:
          </p>

          <div className="d-flex flex-column gap-2">
            {empresas.map((emp) => {
              const empId = String(emp.c_nuges || emp.id);
              const isSelected = String(modalCompanyId) === empId;
              return (
                <div
                  key={empId}
                  onClick={() => setModalCompanyId(empId)}
                  className={`p-3 border rounded-3 d-flex align-items-center justify-content-between ${isSelected ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'border-light-subtle bg-white'}`}
                  style={{ cursor: 'pointer', transition: 'all 0.15s ease-in-out' }}
                >
                  <div className="d-flex align-items-center">
                    <div className={`rounded-circle p-2 me-3 d-flex align-items-center justify-content-center ${isSelected ? 'bg-primary text-white' : 'bg-light text-secondary'}`} style={{ width: '40px', height: '40px' }}>
                      <i className="bi bi-building fs-5"></i>
                    </div>
                    <div>
                      <div className="fw-bold fs-6 text-dark">{emp.nombre || `Empresa ${empId}`}</div>
                    </div>
                  </div>
                  <Form.Check 
                    type="radio" 
                    name="layoutCompanySelect" 
                    checked={isSelected}
                    onChange={() => setModalCompanyId(empId)}
                    className="fs-5"
                  />
                </div>
              );
            })}
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light py-2">
          <Button 
            variant="primary" 
            className="w-100 fw-bold py-2 shadow-sm d-flex align-items-center justify-content-center gap-2"
            onClick={handleConfirmCompanyModal}
          >
            <span>Ingresar a la Empresa</span>
            <i className="bi bi-arrow-right"></i>
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Layout;